import React, { useState } from "react";
import { Clock } from "lucide-react"; // Import Clock icon
import DatePicker from "./categoryDatePicker";
import Dropdown from "./categoryDropdown";
import { addDays } from "date-fns";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux/store";
import { createTimeOffRequest, getTimeOffRequestList } from "../../redux/features/timeoff";

interface TimeData {
    start: string | null;
    end: string | null;
    duration: number; // Store duration in hours
}

interface Detail {
    date: string;
    amount: number;
    start_time: string | null;
    end_time: string | null;
}

interface ICreateTimeOffRequest {
    employee_id: string;
    start_date: string;
    end_date: string;
    category_code: string;
    policy_code?: string;
    details?: Detail[];
    note?: string;
}

const TimeOffForm: React.FC = () => {
    const [formData, setFormData] = useState<ICreateTimeOffRequest>({
        employee_id: "",
        start_date: "",
        end_date: "",
        category_code: "",
        details: [],
        note: "",
    });

    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    const [timeData, setTimeData] = useState<Record<string, TimeData>>({});
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({}); // Track expanded datetime inputs
    const timeoffCategories = useSelector((state: RootState) => state.common.assignedTimeoffCategories);
    const dispatch = useAppDispatch();

    const user = useSelector((state: RootState) => state.user.user);

    const generateDateRange = (start: Date, end: Date): Date[] => {
        const dates: Date[] = [];
        let current = start;
        while (current <= end) {
            dates.push(current);
            current = addDays(current, 1);
        }
        return dates;
    };

    const handleDateSelection = (start: Date, end: Date) => {
        const fullDateRange = generateDateRange(start, end);
        setSelectedDates(fullDateRange);
    };

    const handleTimeChange = (date: string, type: "start" | "end", value: string) => {
        setTimeData((prev) => {
            const updatedTime = { ...prev[date], [type]: value };

            if (updatedTime.start && updatedTime.end) {
                const [startHour] = updatedTime.start.split(":").map(Number);
                const [endHour] = updatedTime.end.split(":").map(Number);

                let durationInHours = endHour - startHour;
                durationInHours = Math.max(durationInHours, 0); // Ensure non-negative duration

                updatedTime.duration = durationInHours;
            }

            return { ...prev, [date]: updatedTime };
        });
    };

    const handleDurationInputChange = (date: string, value: string) => {
        const hours = parseInt(value, 10);
        if (!isNaN(hours) && hours >= 0) {
            setTimeData((prev) => ({
                ...prev,
                [date]: { ...prev[date], duration: hours, start: null, end: null },
            }));
        }
    };

    const toggleExpandRow = (date: string) => {
        setExpandedRows((prev) => ({ ...prev, [date]: !prev[date] }));
    };

    const handleSubmit = async () => {
        const details: Detail[] = selectedDates.map((date) => {
            const formattedDate = date.toISOString().split("T")[0];
            const timeForDate = timeData[date.toDateString()] || { start: null, end: null, duration: 0 };

            return {
                date: formattedDate,
                amount: timeForDate.duration,
                start_time: timeForDate.start,
                end_time: timeForDate.end,
            };
        });

        const finalFormData: ICreateTimeOffRequest = {
            ...formData,
            start_date: selectedDates[0]?.toISOString().split("T")[0],
            end_date: selectedDates[selectedDates.length - 1]?.toISOString().split("T")[0],
            details,
            employee_id: user.id!.toString(),
        };

        await dispatch(createTimeOffRequest(finalFormData));

        dispatch(
            getTimeOffRequestList({
                employee_id: user.id!.toString(),
                start_date: finalFormData.start_date,
                end_date: finalFormData.end_date,
                category_code: ""
            })
        );
    };

    return (
        <form className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
                    <DatePicker onChange={(date) => handleDateSelection(date, selectedDates[selectedDates.length - 1] || date)} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">To</label>
                    <DatePicker onChange={(date) => handleDateSelection(selectedDates[0] || date, date)} />
                </div>
            </div>

            <Dropdown
                label="Time Off Category"
                options={timeoffCategories.map((category) => ({ category_code: category.category_code!, category_name: category.category_name! }))}
                onChange={(e) => setFormData({ ...formData, category_code: e.target.value })}
            />

            <div className="border border-gray-300 rounded-lg p-4 h-72 overflow-y-auto">
                {selectedDates.map((date) => {
                    const dateKey = date.toDateString();
                    return (
                        <div key={dateKey} className="mb-4">
                            <div className="grid grid-cols-6 items-center">
                                <label className="text-gray-700 font-medium">{dateKey}</label>

                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-gray-600">Hours</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={timeData[dateKey]?.duration || ""}
                                        onChange={(e) => handleDurationInputChange(dateKey, e.target.value)}
                                        placeholder="Enter hours"
                                        className="border border-gray-300 rounded-lg px-2 py-1 w-16"
                                    />
                                </div>

                                <button type="button" onClick={() => toggleExpandRow(dateKey)} className="text-gray-500 hover:text-gray-700">
                                    <Clock className="w-5 h-5" />
                                </button>
                            </div>

                            {expandedRows[dateKey] && (
                                <div className="grid grid-cols-3 gap-4 mt-2">
                                    <div>
                                        <label className="text-sm text-gray-600">Start</label>
                                        <input
                                            type="time"
                                            value={timeData[dateKey]?.start || ""}
                                            onChange={(e) => handleTimeChange(dateKey, "start", e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2 w-full"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm text-gray-600">End</label>
                                        <input
                                            type="time"
                                            value={timeData[dateKey]?.end || ""}
                                            onChange={(e) => handleTimeChange(dateKey, "end", e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2 w-full"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <button type="button" className="w-full bg-green-500 text-white py-3 rounded-lg hover:bg-green-600" onClick={handleSubmit}>
                Submit
            </button>
        </form>
    );
};

export default TimeOffForm;